/**
 * Terms and Conditions Service
 * Handles fetching and accepting T&C documents
 */
import config from '../scripts/config';

/**
 * Fetch full T&C document details
 * @param {number} termsId - The T&C document ID
 * @param {function} fetchFn - The authenticated fetch function from backend context
 * @returns {Promise<Object>} T&C document details
 */
export async function fetchTermsAndConditions(termsId, fetchFn) {
    const response = await fetchFn(
        `${config.REACT_APP_HOST}/api/v2/terms-and-conditions/${termsId}`,
        {
            method: 'GET',
            headers: {'Content-Type': 'application/json'},
        }
    );

    if (!response.ok) {
        throw new Error('Failed to fetch terms and conditions');
    }

    return await response.json();
}

/**
 * Accept terms and conditions
 * @param {number} termsId - The T&C document ID to accept
 * @param {function} fetchFn - The authenticated fetch function from backend context
 * @returns {Promise<Object>} Success response
 */
export async function acceptTermsAndConditions(termsId, fetchFn) {
    const response = await fetchFn(
        `${config.REACT_APP_HOST}/api/v2/terms-and-conditions/accept`,
        {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({terms_id: termsId}),
        }
    );

    if (!response.ok) {
        throw new Error('Failed to accept terms and conditions');
    }

    return await response.json();
}

/**
 * Download terms and conditions document from Azure blob storage
 * @param {number} termsId - The T&C document ID to download
 * @param {function} fetchFn - The authenticated fetch function from backend context
 * @returns {Promise<Blob>} PDF blob for download
 */
export async function downloadTermsDocument(termsId, fetchFn) {
    const response = await fetchFn(
        `${config.REACT_APP_HOST}/api/v2/terms-and-conditions/${termsId}/download`,
        {
            method: 'GET',
        }
    );

    if (!response.ok) {
        throw new Error('Failed to download terms and conditions document');
    }

    return await response.blob();
}

/**
 * Get download URL for a terms and conditions document
 * @param {number} termsId - The T&C document ID
 * @returns {string} Download URL
 */
export function getTermsDownloadUrl(termsId) {
    return `${config.REACT_APP_HOST}/api/v2/terms-and-conditions/${termsId}/download`;
}

/**
 * Fetch historical terms and conditions by document type
 * @param {function} fetchFn - The authenticated fetch function from backend context
 * @param {string} docType - Document type ('platform' or 'commissioning')
 * @returns {Promise<Array>} Array of historical T&C documents
 */
export async function fetchHistoricalTerms(fetchFn, docType) {
    const response = await fetchFn(
        `${config.REACT_APP_HOST}/api/v2/terms-and-conditions/historical?doc_type=${docType}`
    );

    if (!response.ok) {
        throw new Error(`Failed to fetch historical ${docType} terms and conditions`);
    }

    const data = await response.json();
    
    // Check if response contains an error
    if (data && data.error) {
        throw new Error(data.error);
    }
    
    // Extract historical_versions array and transform field names
    const versions = data.historical_versions || [];
    
    return versions.map(v => ({
        ID: v.id,
        DOC_VERSION: v.version,
        DOC_TITLE: v.title,
        PDF_URL: getTermsDownloadUrl(v.id),
        FROM_DATE: v.from_date,
        TO_DATE: v.to_date,
        DATE_CREATED: v.date_created,
        DOC_TEXT_RTF: v.doc_text_rtf,
        IS_ACTIVE: v.is_active
    }));
}
